<template>
    <SearchResultTable
        :columns="columns"
        :data="data"
        :pagination="pagination"
        :isLoading="isLoading"
        @onPageIndexChange="onPageIndexChange"
        @onPageSizeChange="onPageSizeChange"
        @downloadExcelFile="validateExcelFileDownload"
    >
        <template #rightBtnGroup>
            <NButton
                type="primary"
                size="large"
                strong
                @click="openPointProvideRequestPopup"
                >지급요청</NButton
            >
            <NButton
                type="primary"
                size="large"
                strong
                @click="openPointRetrieveRequestPopup"
                >회수요청</NButton
            >
        </template>
    </SearchResultTable>

    <!-- 포인트 지급 요청 Modal -->
    <PointProvideRequestPopup
        v-if="isOpenPointProvideRequestPopup"
        :isOpen="isOpenPointProvideRequestPopup"
        @close="closePointProvideRequestPopup"
        @refresh="onPageIndexChange(1)"
    />

    <!-- 포인트 회수 요청 Modal -->
    <PointRetrieveRequestPopup
        v-if="isOpenPointRetrieveRequestPopup"
        :isOpen="isOpenPointRetrieveRequestPopup"
        @close="closePointRetrieveRequestPopup"
        @refresh="onPageIndexChange(1)"
    />

    <!-- 포인트 지급/회수 요청 상세내역 조회 Modal -->
    <PointRequestDetailViewPopup
        v-if="isOpenPointRequestDetailViewPopup && selectedRow?.id"
        :isOpen="isOpenPointRequestDetailViewPopup"
        :id="selectedRow?.id"
        :serviceType="serviceType"
        :provideType="selectedRow?.provideType"
        @close="closePointRequestDetailViewPopup"
        @refresh="onPageIndexChange(pagination.page)"
    />

    <!-- 포인트 지급/회수 요청 수정 및 취소 Modal -->
    <PointRequestDetailEditPopup
        v-if="isOpenPointRequestDetailEditPopup && selectedRow?.id"
        :isOpen="isOpenPointRequestDetailEditPopup"
        :id="selectedRow?.id"
        :serviceType="serviceType"
        :provideType="selectedRow?.provideType"
        @close="closePointRequestDetailEditPopup"
        @refresh="onPageIndexChange(pagination.page)"
    />

    <!-- Excel 다운로드 시 비밀번호 2차 인증을 위한 Popup -->
    <PasswordVerificationPopup
        v-if="isOpenPasswordVerificationPopup"
        :isOpen="isOpenPasswordVerificationPopup"
        @submit="downloadExcel"
        @close="closePasswordVerificationPopup"
    />
</template>

<script setup lang="ts">
import {getPointRequestListExcelFile as getPointRequestListExcelFileApi} from '@/api/points/point-request-history-api';
import InfoSvg from '@/assets/svg/icn_info.svg?component';
import Base3dButton from '@/components/common/input/Base3dButton.vue';
import SearchResultTable from '@/components/common/table/SearchResultTable.vue';
import PointRequestDetailEditPopup from '@/components/points/request-detail/popup/PointRequestDetailEditPopup.vue';
import PointRequestDetailViewPopup from '@/components/points/request-detail/popup/PointRequestDetailViewPopup.vue';
import PointProvideRequestPopup from '@/components/points/request-history/popup/PointProvideRequestPopup.vue';
import PointRetrieveRequestPopup from '@/components/points/request-history/popup/PointRetrieveRequestPopup.vue';
import PasswordVerificationPopup from '@/components/popup/PasswordVerification.vue';
import {priceFilter} from '@/filter/number-filter';
import useUserStore from '@/store/user-store';
import {PointRequestHistoryContentI} from '@/types/points/api/point-request-history-api-types';
import {
    RequestStatusKorE,
    TargetServiceTypeE,
    TargetStatusE,
} from '@/types/points/point-keyword-types';
import {PointRequestHistorySearchFormStateI} from '@/types/points/point-request-history-types';
import {
    EXCEL_DOWNLOAD_AUTH_EXPIRED,
    EXCEL_DOWNLOAD_FAILED,
    WRONG_PASSWORD,
} from '@/utils/message';
import * as Sentry from '@sentry/vue';
import {AxiosError} from 'axios';
import {saveAs} from 'file-saver';
import {isEmpty} from 'lodash-es';
import {NButton, NIcon, NTooltip, type DataTableColumns} from 'naive-ui';
import {storeToRefs} from 'pinia';
import {useFormValues} from 'vee-validate';
import {computed, h, ref} from 'vue';
import {useRouter} from 'vue-router';
import {useOnOff} from 'yeogi-utils/hooks/useOnOff';

// props, emits, libs
defineProps<{
    data: PointRequestHistoryContentI[];
    pagination: {
        page: number; // 현재 페이지
        pageSize: number; // 한 페이지당 보이는 데이터 수
        pageCount?: number; // 총 페이지 수
        itemCount: number; // 총 데이터 수
    };
    isLoading: boolean;
}>();

const emits = defineEmits<{
    (e: 'onPageIndexChange', currentPageIndex: number): void;
    (e: 'onPageSizeChange', pageSize: number): void;
}>();

const userStore = useUserStore();
const router = useRouter();

const formState = useFormValues<PointRequestHistorySearchFormStateI>();

// 포인트 지급 요청 Modal
const [
    isOpenPointProvideRequestPopup,
    openPointProvideRequestPopup,
    closePointProvideRequestPopup,
] = useOnOff(false);

// 포인트 회수 요청 Modal
const [
    isOpenPointRetrieveRequestPopup,
    openPointRetrieveRequestPopup,
    closePointRetrieveRequestPopup,
] = useOnOff(false);

// 비밀번호 2차 인증 Popup
const [
    isOpenPasswordVerificationPopup,
    openPasswordVerificationPopup,
    closePasswordVerificationPopup,
] = useOnOff(false);

// 포인트 지급/회수 요청 상세내역 조회 Modal
const [
    isOpenPointRequestDetailViewPopup,
    openPointRequestDetailViewPopup,
    closePointRequestDetailViewPopup,
] = useOnOff(false);

// 포인트 지급/회수 요청 수정 및 취소 Modal
const [
    isOpenPointRequestDetailEditPopup,
    openPointRequestDetailEditPopup,
    closePointRequestDetailEditPopup,
] = useOnOff(false);

// ref, reactive
const {isExcelDownloadVerified} = storeToRefs(userStore);

const selectedRow = ref(null as null | PointRequestHistoryContentI);

const columns: DataTableColumns<PointRequestHistoryContentI> = [
    {
        title: '요청번호',
        key: 'id',
        align: 'center',
        width: 120,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        render(rowData) {
            return rowData.id ?? '-';
        },
    },
    {
        title: '지급/회수 형태',
        key: 'provideType',
        align: 'center',
        width: 130,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        className: 'font-bold',
        render(rowData) {
            return rowData.provideType ?? '-';
        },
    },
    {
        title: '지급/회수 사유',
        key: 'description',
        titleAlign: 'center',
        width: 300,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        render(rowData) {
            return rowData.description ?? '-';
        },
    },
    {
        title: '요청건수',
        key: 'uploadedCount',
        align: 'center',
        width: 130,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        className: 'text-primary',
        render(rowData) {
            selectedRow.value = rowData;
            const uploadedCount = rowData.uploadedCount;

            const goToPointTargetList = (e: Event) => {
                if (isEmpty(rowData)) return e.preventDefault();

                // NOTE: 지급/회수 대상자 목록 페이지로 이동 (조회조건: 전체 조회)
                router.push({
                    path: '/points/target',
                    query: {
                        provideId: rowData.id,
                        status: 'ALL',
                        page: 1,
                        size: 10,
                    },
                });
            };

            return h(
                'span',
                {
                    className: 'cell-link',
                    onClick: (e: Event) => goToPointTargetList(e),
                },
                {default: () => `${uploadedCount}건`},
            );
        },
    },
    {
        title: '가입대기건수',
        key: 'waitingCount',
        align: 'center',
        width: 130,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        className: 'text-warning',
        render(rowData) {
            selectedRow.value = rowData;
            const waitingCount = rowData.waitingCount;

            const goToPointTargetList = (e: Event) => {
                if (isEmpty(rowData)) return e.preventDefault();

                // NOTE: 지급/회수 대상자 목록 페이지로 이동 (조회조건: 가입대기 상태)
                router.push({
                    path: '/points/target',
                    query: {
                        provideId: rowData.id,
                        status: TargetStatusE.UNSIGNED,
                        page: 1,
                        size: 10,
                    },
                });
            };

            return h(
                'span',
                {
                    className: 'cell-link',
                    onClick: (e: Event) => goToPointTargetList(e),
                },
                {default: () => `${waitingCount}건`},
            );
        },
    },
    {
        title: '성공건수',
        key: 'successCount',
        align: 'center',
        width: 130,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        className: 'text-success',
        render(rowData) {
            selectedRow.value = rowData;
            const successCount = rowData.successCount;

            const goToPointTargetList = (e: Event) => {
                if (isEmpty(rowData)) return e.preventDefault();

                // NOTE: 지급/회수 대상자 목록 페이지로 이동 (조회조건: 처리완료 상태)
                router.push({
                    path: '/points/target',
                    query: {
                        provideId: rowData.id,
                        status: TargetStatusE.FINISHED,
                        page: 1,
                        size: 10,
                    },
                });
            };

            return h(
                'span',
                {
                    className: 'cell-link',
                    onClick: (e: Event) => goToPointTargetList(e),
                },
                {default: () => `${successCount}건`},
            );
        },
    },
    {
        title: '실패건수',
        key: 'failCount',
        align: 'center',
        width: 130,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        className: 'text-danger',
        render(rowData) {
            selectedRow.value = rowData;
            const failCount = rowData.failCount;

            const goToPointTargetList = (e: Event) => {
                if (isEmpty(rowData)) return e.preventDefault();

                // NOTE: 지급/회수 대상자 목록 페이지로 이동 (조회조건: 처리실패 상태)
                router.push({
                    path: '/points/target',
                    query: {
                        provideId: rowData.id,
                        status: TargetStatusE.FAIL,
                        page: 1,
                        size: 10,
                    },
                });
            };

            return h(
                'span',
                {
                    className: 'cell-link',
                    onClick: (e: Event) => goToPointTargetList(e),
                },
                {default: () => `${failCount}건`},
            );
        },
    },
    {
        title: '요청자',
        key: 'requestor',
        titleAlign: 'center',
        width: 200,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        render(rowData) {
            return rowData.requestor ?? '-';
        },
    },
    {
        title: '등록일시',
        key: 'createDate',
        align: 'center',
        width: 180,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        render(rowData) {
            return rowData.createDate ?? '-';
        },
    },
    {
        title: '지급예약일시',
        key: 'reserveDate',
        align: 'center',
        width: 180,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        render(rowData) {
            return rowData.reserveDate ?? '-';
        },
    },
    {
        title: '총 신청포인트',
        key: 'totalPoint',
        titleAlign: 'center',
        align: 'right',
        width: 200,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        render(rowData) {
            const totalPoint = rowData.totalPoint;

            return totalPoint ? priceFilter(totalPoint, {unit: 'P'}) : '-';
        },
    },
    {
        title: '실 처리포인트',
        key: 'totalSuccessPoint',
        titleAlign: 'center',
        align: 'right',
        width: 200,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        render(rowData) {
            const totalSuccessPoint = rowData.totalSuccessPoint;

            return totalSuccessPoint
                ? priceFilter(totalSuccessPoint, {unit: 'P'})
                : '-';
        },
    },
    {
        title: '지급대기기간',
        key: 'limitedDate',
        align: 'center',
        width: 150,
        resizable: true,
        ellipsis: {
            tooltip: true,
        },
        render(rowData) {
            const limitedDate = rowData.limitedDate;

            return limitedDate ? `~ ${limitedDate}` : '-';
        },
    },
    {
        title: () =>
            h(
                'div',
                {
                    style: 'display: flex; justify-content: center; align-items: center;',
                },
                [
                    [
                        h('span', '처리상태'),
                        [
                            h(
                                NTooltip,
                                {
                                    trigger: 'hover',
                                    style: 'max-width: 250px; white-space: pre-wrap;',
                                },
                                {
                                    default: () =>
                                        `뱃지를 클릭해 보세요.\n요청내역의 확인/수정/취소가 가능해요.`,
                                    trigger: () =>
                                        h(
                                            NIcon,
                                            {
                                                size: 20,
                                                style: 'margin-left: 5px; cursor: help;',
                                            },
                                            {
                                                default: () => h(InfoSvg),
                                            },
                                        ),
                                },
                            ),
                        ],
                    ],
                ],
            ),
        key: 'provideStatus',
        align: 'center',
        fixed: 'right',
        width: 120,
        render(rowData) {
            const provideStatus = rowData.provideStatus;

            const btnThemeStyle = () => {
                switch (provideStatus) {
                    case RequestStatusKorE.처리중:
                    case RequestStatusKorE.승인:
                        return 'confirm';
                    case RequestStatusKorE.반려:
                        return 'warning';
                    case RequestStatusKorE.요청취소:
                        return 'danger';
                    case RequestStatusKorE.처리완료:
                        return 'success';
                    case RequestStatusKorE.요청대기:
                    default:
                        return 'secondary';
                }
            };

            const openPointRequestDetailPopup = (e: Event) => {
                if (!rowData) return e.preventDefault();

                selectedRow.value = rowData;

                if (provideStatus === RequestStatusKorE.요청대기) {
                    // 수정 팝업 오픈
                    openPointRequestDetailEditPopup();
                } else {
                    // 조회 팝업 오픈
                    openPointRequestDetailViewPopup();
                }
            };

            return h(
                Base3dButton,
                {
                    value: provideStatus,
                    theme: btnThemeStyle(),
                    onClick: (e: Event) => openPointRequestDetailPopup(e),
                },
                {default: () => provideStatus},
            );
        },
    },
];

// computed
const serviceType = computed(() => {
    switch (selectedRow.value?.provideType) {
        case '회수':
            return TargetServiceTypeE.RETRIEVE;
        case '즉시지급':
        case '예약지급':
            return TargetServiceTypeE.PROVIDE;
        default:
            return '';
    }
});

// methods
const onPageIndexChange = (currentPageIndex: number) => {
    emits('onPageIndexChange', currentPageIndex);
};

const onPageSizeChange = (pageSize: number) => {
    emits('onPageSizeChange', pageSize);
};

/**
 * @description Excel 파일 다운로드 API 요청전 유효성 체크
 */
const validateExcelFileDownload = () => {
    // NOTE: 포인트 지급/회수 요청내역 Excel 다운로드 API에서 기간(fromDate, toDate)는 required 필드임
    if (isEmpty(formState.value.fromDate || formState.value.toDate)) {
        return window.customAlert('날짜/기간을 선택해주세요.');
    }

    if (isExcelDownloadVerified.value) {
        // 필수값 모두 준비 & 엑셀 다운로드 2차인증 된 상태이면, 바로 엑셀 다운로드 진행
        downloadExcel();
    } else {
        // 필수값 모두 준비 & 엑셀 다운로드 2차인증 안된 상태이면, 비밀번호 인증창 오픈
        openPasswordVerificationPopup();
    }
};

/**
 * @description 비밀번호 인증 및 포인트 지급/요청 내역 Excel 다운로드 요청 함수
 * 비밀번호 인증 Modal에서 submit 버튼 클릭시 발화됨
 * @param password - 비밀번호 인증 Modal에서 입력받은 비밀번호. 비밀번호 인증 및 포인트 지급/요청 내역 Excel 다운로드 요청시 사용됨
 * @param setErrors - 비밀번호 필드 vee-validate setError 메서드
 */
const downloadExcel = async (
    password?: string, // FIXME: 비번 입력안할때 용도로 수정
    setErrors?: (messages: Record<string, string>) => void, // FIXME: 비번 입력안할때 용도로 수정
) => {
    try {
        const content = await getPointRequestListExcelFileApi(
            {
                fromDate: formState.value.fromDate as string, // required
                toDate: formState.value.toDate as string, // required
                periodType: formState.value.periodType,
                ...(formState.value.provideType !== 'ALL' && {
                    provideType: formState.value.provideType,
                }),
                ...(formState.value.provideStatus !== 'ALL' && {
                    provideStatus: formState.value.provideStatus,
                }),
            },
            password,
        );

        alert(
            '포인트 지급/회수 요청내역 Excel 파일 다운로드가 완료되었습니다.',
        );
        saveAs(
            new Blob([content]),
            '여기어때_포인트_지급_및_회수_요청내역.xlsx',
        );

        if (!isExcelDownloadVerified.value) {
            // 다운로드 완료 후, 쿠키에 플래그 없거나 false이면 저장해주기
            userStore.updateExcelDownloadVerified('true');
        }

        if (isOpenPasswordVerificationPopup.value) {
            // 비밀번호 팝업창 오픈되어 있을때는 닫아주기
            closePasswordVerificationPopup();
        }
    } catch (err) {
        const axiosError = err as AxiosError;

        Sentry.captureException(axiosError, {
            level: 'error',
            extra: {
                code: axiosError.code,
                status: axiosError.response?.status,
                request: axiosError.request,
                response: axiosError.response?.data,
                message: axiosError.message,
                from: 'src/components/points/request-history/PointRequestHistorySearchResult.vue > downloadExcel',
            },
        });

        if (
            setErrors &&
            isOpenPasswordVerificationPopup.value &&
            axiosError.response?.status === 400
        ) {
            // NOTE: 비밀번호 틀렸을때에는 에러 alert 노출X
            return setErrors({password: WRONG_PASSWORD});
        }

        if (axiosError.response?.status === 401) {
            // NOTE: 서버 측 인증상태 & 브라우저에 저장한 엑셀 다운로드 2차 인증 여부 플래그 sync가 맞지 않을때 (401일 때), sync를 맞춰주기 위해 엑셀 다운로드 2차 인증 여부 플래그를 false로 변경 후 2차 인증 재유도

            window.customAlert(EXCEL_DOWNLOAD_AUTH_EXPIRED, () => {
                userStore.updateExcelDownloadVerified('false');

                openPasswordVerificationPopup();
            });

            return;
        }

        window.customAlert(
            `[${axiosError.response?.status}] ${axiosError.response?.statusText}: ${axiosError.message}` ||
                EXCEL_DOWNLOAD_FAILED,
        );
    }
};
</script>

<style lang="scss" scoped>
:deep() {
    @media (max-width: 720px) {
        .data-managing-btn-wrap {
            flex-direction: column;
        }

        .managing-btn-group {
            &.right {
                margin-top: 10px;
            }
        }
    }
}
</style>
